import { Ellipsis, LockKeyhole, X } from 'lucide-react';
import system_setting_icon from 'images/setting_icons/system_setting_icon.svg';
import * as React from 'react';
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd';
import { useRecoilValue } from 'recoil';
import styled from 'styled-components';
import { AFormFields, Field } from '../../../atoms/forms';
import Add from '../../../components_v2/add/Add';
import Dropdown from '../../../components_v2/dropdown/Dropdown';
import { DropdownData } from '../../../components_v2/dropdown/model/Model';
import Input from '../../../components_v2/input/Input';
import { PopupMode } from '../../../components_v2/popup/model/Model';
import Popup from '../../../components_v2/popup/Popup';
import PopupCreation from '../../../components_v2/popup/PopupCreation';
import { CreationPopupContainer, creationPopupDropdownStyle, creationPopupInputStyle, Label } from '../../../components_v2/popup/style/CreationFormStyle';
import { Column, Table } from '../../../components_v2/table/Table';
import { isSuperAdmin } from '../../../components_v2/utils';
import { BorderColor, DarkGreySidely, DarkGreySidely2, GreySidely3, RedSidely, SurfaceColor } from '../../../styles/global/css/Utils';
import { Translate, translateToNode, translateToString } from '../../../styles/global/translate';
import { AlertRes } from '../../alert/AlertProvider';
import useAlert from '../../alert/UseAlert';
import { launchToastError } from '../../forms/visual-form-editor/visualFormEditor';
import { ToolbarImageContainer } from '../../globals/defaultToolbar/style/Style';
import { ToolbarState } from '../../globals/mainPage/mainPage';
import { LoadingStateEnum } from '../../import/model';
import { TableRow } from '../../orders/templateOrders/style/Style';
import PermissionContext from '../../permissions/PermissionContext';
import Restricted from '../../permissions/Restricted';
import RestrictedSuperAdmin from '../../permissions/RestrictedSuperAdmin';
import { ModalState } from '../../products/model';
import { FlexDiv } from '../../products/style';
import { AdditionalValuesPath, AdditionalValuesPathBody, deleteAdditionalValuesPath, getAdditionalValuesPaths, postAdditionalValuesPath, putAdditionalValuesPath } from './actions';
import { DandDDots, PreventModif, WarningLock } from './statusesAndTagsSettings';

const CellText = styled(TableRow)`
	white-space: nowrap;
`;

const SectionLabel = styled(Label)`
	margin: 0;
	line-height: 21px;
`;

const DimensionRow = styled(FlexDiv)`
	border: 1px solid ${BorderColor};
	border-radius: 5px;
	padding: 4px 8px;
	font-size: 12px;
	color: ${DarkGreySidely2};
	background-color: ${SurfaceColor};
`;

const DimensionName = styled.span`
	flex: 1;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

const IconButton = styled.button`
	border: none;
	background: none;
	padding: 0;
	display: flex;
	cursor: pointer;
	color: ${DarkGreySidely2};
`;

function parseOptions(text: string): { ok: true, value: object | null } | { ok: false } {
	if (text.trim().length === 0) return { ok: true, value: null };
	try {
		const value = JSON.parse(text);
		if (typeof value !== 'object' || value === null) return { ok: false };
		return { ok: true, value };
	} catch {
		return { ok: false };
	}
}

function fieldName(fields: Map<number, Field>, id: number): string {
	return fields.get(id)?.name ?? `#${id}`;
}

function getColumns(fields: Map<number, Field>, onDelete: (path: AdditionalValuesPath) => void, onOpen: (path: AdditionalValuesPath) => void, onToggleLock: (path: AdditionalValuesPath) => void, deleteAllowed: boolean, updateAllowed: boolean, superAdmin: boolean, anyLocked: boolean): Column<AdditionalValuesPath>[] {
	const cols: Column<AdditionalValuesPath>[] = [
		{
			id: 'label',
			Header: translateToString('multidimensional_assortments.label'),
			accessor: row => <CellText onClick={() => updateAllowed && !row.locked && onOpen(row)} cursor={updateAllowed && !row.locked ? 'pointer' : undefined} fontWeight='500' color={DarkGreySidely}>{row.label}</CellText>,
			width: undefined,
			toolTip: row => row.label
		},
		{
			id: 'field',
			Header: translateToString('multidimensional_assortments.field'),
			accessor: row => <CellText>{fieldName(fields, row.field_id)}</CellText>,
			width: undefined,
			toolTip: row => fieldName(fields, row.field_id)
		},
		{
			id: 'dimensions',
			Header: translateToString('multidimensional_assortments.dimensions'),
			accessor: row => <CellText>{row.additional_column_ids.map(id => fieldName(fields, id)).join(' › ')}</CellText>,
			width: undefined,
			toolTip: row => row.additional_column_ids.map(id => fieldName(fields, id)).join(' › '),
			disableSortBy: true,
		},
	];
	if (superAdmin) {
		cols.push({
			id: 'options',
			Header: translateToString('multidimensional_assortments.options'),
			accessor: row => <CellText>{row.options ? JSON.stringify(row.options) : '-'}</CellText>,
			width: undefined,
			toolTip: row => row.options ? JSON.stringify(row.options, null, 2) : undefined,
			disableSortBy: true,
		});
	}
	if (superAdmin || anyLocked) {
		cols.push({
			id: 'lock',
			Header: '',
			accessor: row => <FlexDiv gap='6px'>
				{superAdmin && <PreventModif locked={row.locked} onClick={() => onToggleLock(row)}><LockKeyhole size={18} /></PreventModif>}
				{row.locked && <WarningLock title={translateToString('report_editor.contact_sidely_modify')}><LockKeyhole size={18} /></WarningLock>}
			</FlexDiv>,
			width: 70,
			minWidth: 70,
			disableSortBy: true,
			disableFilter: true,
			unresizeable: true,
		});
	}
	const actions: DropdownData<'EDIT' | 'DELETE'>[] = [
		...(updateAllowed ? [{ label: translateToString('global.quick_action.edit'), value: 'EDIT' as const }] : []),
		...(deleteAllowed ? [{ label: translateToString('global.quick_action.delete'), value: 'DELETE' as const }] : []),
	];
	if (actions.length > 0) {
		cols.push({
			id: 'quick_actions',
			noHeaderEllipsis: true,
			Header: <img src={system_setting_icon} style={{ width: 18 }} />,
			accessor: row => row.locked ? <></> : <Dropdown
				name='quick_action'
				datalist={actions}
				readOnly
				searchable={false}
				dropdownStyle={{ width: '10px', height: '18px', containerHeight: '18px', optionWidth: '100px', fontSize: 13, optionLeft: '-112px', containerTop: '-6px' }}
				JSXButton={() => <ToolbarImageContainer><Ellipsis color={GreySidely3} strokeWidth={2.5} size={18} /></ToolbarImageContainer>}
				onChange={(value: DropdownData<'EDIT' | 'DELETE'>) => {
					if (value.value === 'EDIT') onOpen(row);
					else if (value.value === 'DELETE') onDelete(row);
				}}
			/>,
			width: 40,
			minWidth: 40,
			disableSortBy: true,
			disableFilter: true,
			unresizeable: true,
			freeze: 'right'
		});
	}
	return cols.map(col => ({ ...col, padding: 6 }));
}

export default function MultidimensionalAssortmentsManagement(props: {
	setToolBarState: (value: ToolbarState) => void
}) {
	const allFields = useRecoilValue(AFormFields);
	const assortmentFields = React.useMemo(() => allFields.filter(f => f.is_additional).sort((a, b) => a.name.localeCompare(b.name)), [allFields]);
	const fieldsById = React.useMemo(() => new Map(allFields.map(f => [f.id, f])), [allFields]);
	const [paths, setPaths] = React.useState<AdditionalValuesPath[]>([]);
	const [creationOpen, setCreationOpen] = React.useState<ModalState<AdditionalValuesPath>>({ isOpen: false });
	const alert = useAlert();
	const { isAllowedTo } = React.useContext(PermissionContext);
	const superAdmin = isSuperAdmin();

	const fetchPaths = React.useCallback(() => getAdditionalValuesPaths().then(setPaths).catch(console.error), []);

	React.useEffect(() => {
		fetchPaths();
		props.setToolBarState({
			bottomRightToolbarComponent: <FlexDiv gap='10px'>
				<Restricted to={{ objectAction: 'CreateCompanySettings' }}><Add size='22px' onClick={() => setCreationOpen({ isOpen: true })} /></Restricted>
			</FlexDiv>
		});
	}, []);

	const onDelete = React.useCallback((path: AdditionalValuesPath) => {
		alert({
			title: translateToString('multidimensional_assortments.delete_title'),
			content: <div>{translateToNode('multidimensional_assortments.delete_content', { variables: [['label', path.label]] })}</div>,
			mode: 'delete'
		}).then(res => {
			if (res.res !== AlertRes.Ok) return;
			deleteAdditionalValuesPath(path.id).then(fetchPaths).catch(() => launchToastError(translateToString('error_please_retry')));
		});
	}, []);

	const onToggleLock = React.useCallback((path: AdditionalValuesPath) => {
		const { id, ...body } = path;
		putAdditionalValuesPath(id, { ...body, locked: !path.locked }).then(fetchPaths).catch(() => launchToastError(translateToString('error_please_retry')));
	}, []);

	const anyLocked = paths.some(path => path.locked);
	const columns = React.useMemo(() => getColumns(
		fieldsById,
		onDelete,
		data => setCreationOpen({ isOpen: true, data }),
		onToggleLock,
		isAllowedTo({ objectAction: 'DeleteCompanySettings' }),
		isAllowedTo({ objectAction: 'UpdateCompanySettings' }),
		superAdmin,
		anyLocked
	), [fieldsById, superAdmin, anyLocked]);

	return <>
		<Table
			noBorder
			height='100%'
			columns={columns}
			data={paths}
		/>
		<Popup
			isOpen={creationOpen.isOpen}
			onClickOut={() => setCreationOpen({ isOpen: false })}
			popupStyle={{ animate: true, height: '100%', top: '0%' }}
			popupMode={PopupMode.Details}
			content={<PathCreation
				fields={assortmentFields}
				path={creationOpen.data}
				onClose={() => setCreationOpen({ isOpen: false })}
				onCreate={() => {
					setCreationOpen({ isOpen: false });
					fetchPaths();
				}}
			/>}
		/>
	</>;
}

function PathCreation(props: { fields: Field[], path?: AdditionalValuesPath, onClose: () => void, onCreate: () => void }) {
	const { path, fields } = props;
	const [label, setLabel] = React.useState<string>(path?.label ?? '');
	const [fieldId, setFieldId] = React.useState<number | undefined>(path?.field_id);
	const [dimensionIds, setDimensionIds] = React.useState<number[]>(path?.additional_column_ids ?? []);
	const [optionsText, setOptionsText] = React.useState<string>(path?.options ? JSON.stringify(path.options, null, 2) : '');
	const [loadingState, setLoadingState] = React.useState<LoadingStateEnum>();

	React.useEffect(() => {
		setLabel(path?.label ?? '');
		setFieldId(path?.field_id);
		setDimensionIds(path?.additional_column_ids ?? []);
		setOptionsText(path?.options ? JSON.stringify(path.options, null, 2) : '');
	}, [path]);

	const fieldOptions: DropdownData<Field>[] = React.useMemo(() => fields.map(f => ({ label: f.name, value: f })), [fields]);
	const dimensionOptions = React.useMemo(() => fieldOptions.filter(o => o.value.id !== fieldId && !dimensionIds.includes(o.value.id)), [fieldOptions, fieldId, dimensionIds]);
	const options = parseOptions(optionsText);
	const canValidate = label.trim().length > 0 && fieldId !== undefined && dimensionIds.length > 0 && options.ok;

	const onDragEnd = ({ source, destination }: DropResult) => {
		if (!destination || source.index === destination.index) return;
		setDimensionIds(ids => {
			const next = [...ids];
			const [moved] = next.splice(source.index, 1);
			next.splice(destination.index, 0, moved);
			return next;
		});
	};

	const submit = async() => {
		if (!canValidate || fieldId === undefined || !options.ok) return;
		const body: AdditionalValuesPathBody = { label: label.trim(), field_id: fieldId, additional_column_ids: dimensionIds, options: options.value };
		setLoadingState(LoadingStateEnum.LOADING);
		try {
			if (path) await putAdditionalValuesPath(path.id, body);
			else await postAdditionalValuesPath(body);
			setLoadingState(LoadingStateEnum.LOADED);
			props.onCreate();
		} catch (e) {
			console.error(e);
			setLoadingState(LoadingStateEnum.ERROR);
			launchToastError(translateToString('error_please_retry'));
		}
	};

	return <PopupCreation
		title={translateToString(path ? 'multidimensional_assortments.edit' : 'multidimensional_assortments.create')}
		canValidate={canValidate}
		onSubmit={submit}
		loading={loadingState}
		onClose={props.onClose}
		messageButton={path ? <Translate id='global.edit' /> : <Translate id='global.create' />}
	>
		<CreationPopupContainer gap='12px'>
			<Input
				inputStyle={creationPopupInputStyle}
				name='label'
				type='text'
				placeholder={translateToString('multidimensional_assortments.label')}
				label={translateToString('multidimensional_assortments.label')}
				onChange={setLabel}
				required
				value={label}
			/>
			<Dropdown
				label={translateToString('multidimensional_assortments.field')}
				dropdownStyle={creationPopupDropdownStyle}
				readOnly
				required
				datalist={fieldOptions}
				selectedValue={fieldOptions.find(o => o.value.id === fieldId)}
				onChange={(value: DropdownData<Field>) => {
					setFieldId(value.value.id);
					setDimensionIds(ids => ids.filter(id => id !== value.value.id));
				}}
				name='path_field'
			/>
			<FlexDiv flow='column' align='stretch' gap='6px'>
				<SectionLabel required>{translateToString('multidimensional_assortments.dimensions')}</SectionLabel>
				{dimensionIds.length > 0 && <DragDropContext onDragEnd={onDragEnd}>
					<Droppable droppableId='dimensions'>
						{provided => <FlexDiv flow='column' align='stretch' gap='6px' innerRef={provided.innerRef} {...provided.droppableProps}>
							{dimensionIds.map((id, index) => <Draggable key={id} draggableId={id.toString()} index={index}>
								{provided => <DimensionRow gap='6px' innerRef={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
									<DandDDots height='14px' />
									<DimensionName>{index + 1}. {fields.find(f => f.id === id)?.name ?? `#${id}`}</DimensionName>
									<IconButton type='button' onClick={() => setDimensionIds(ids => ids.filter(i => i !== id))}><X size={14} /></IconButton>
								</DimensionRow>}
							</Draggable>)}
							{provided.placeholder}
						</FlexDiv>}
					</Droppable>
				</DragDropContext>}
				<Dropdown
					key={dimensionIds.length}
					dropdownStyle={creationPopupDropdownStyle}
					readOnly
					placeholder={translateToString('multidimensional_assortments.add_dimension')}
					datalist={dimensionOptions}
					onChange={(value: DropdownData<Field>) => setDimensionIds(ids => [...ids, value.value.id])}
					name='path_dimension'
				/>
			</FlexDiv>
			<RestrictedSuperAdmin>
				<Input
					textArea
					inputStyle={creationPopupInputStyle}
					name='options'
					type='text'
					placeholder='{}'
					label={translateToString('multidimensional_assortments.options')}
					onChange={setOptionsText}
					value={optionsText}
				/>
				{!options.ok && <SectionLabel style={{ color: RedSidely }}>{translateToString('multidimensional_assortments.invalid_json')}</SectionLabel>}
			</RestrictedSuperAdmin>
		</CreationPopupContainer>
	</PopupCreation>;
}
